/**
 * Audit Log Utility Functions
 * Provides client-side logging capabilities for administrative actions
 * stored in browser LocalStorage for security auditing and history tracking.
 */

// Storage key used to persist audit log records in LocalStorage
const AUDIT_LOG_KEY = 'clipstone_admin_audit_log';
const AUDIT_CLEAR_HISTORY_KEY = 'clipstone_admin_audit_clear_history';
const MAX_AUDIT_ENTRIES = 500;
const MAX_CLEAR_HISTORY = 100;

/**
 * Safely parses a JSON string with a fallback value on parse failure.
 * @param {string} value - JSON string to parse
 * @param {*} fallback - Default return value if parsing fails
 * @returns {*} Parsed object or fallback
 */
const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Retrieves all stored audit log entries, sorted by timestamp descending.
 * @returns {Array<Object>} Sorted audit log records
 */
export const getAuditLogEntries = () => {
  // Ensure function runs safely in browser environments
  if (typeof window === 'undefined') return [];

  const stored = window.localStorage.getItem(AUDIT_LOG_KEY);
  const entries = Array.isArray(safeParse(stored || '[]', []))
    ? safeParse(stored || '[]', [])
    : [];

  // Sort newest first based on ISO timestamp
  return entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

/**
 * Appends a new audit log record to LocalStorage.
 * @param {Object} params - Audit event parameters
 * @param {string} params.actorEmail - Email of administrator performing the action
 * @param {string} params.action - Description of action taken (e.g. 'Created', 'Deleted')
 * @param {string} params.entityType - Type of target entity (e.g. 'Event', 'User')
 * @param {string} params.entityName - Name or title of affected entity
 * @param {string} params.details - Additional contextual details
 * @param {string} [params.outcome='success'] - Outcome of action ('success' or 'failed')
 */
export const appendAuditLogEntry = ({
  actorEmail,
  action,
  entityType,
  entityName,
  details,
  outcome = 'success',
}) => {
  if (typeof window === 'undefined') return;

  // Construct structured audit record with unique ID and timestamp
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    actorEmail: actorEmail || 'Unknown admin',
    action: action || 'Updated',
    entityType: entityType || 'item',
    entityName: entityName || 'Unnamed',
    details: details || '',
    outcome,
  };

  // Fetch current records, prepend new entry, and enforce entry limit
  const current = getAuditLogEntries();
  const next = [entry, ...current].slice(0, MAX_AUDIT_ENTRIES);
  window.localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(next));

  // Notify any open admin dashboard instances to refresh their audit log display.
  window.dispatchEvent(new Event('audit-log-updated'));
};

export const clearAuditLogEntries = (actorEmail = 'Unknown admin') => {
  if (typeof window === 'undefined') return;

  // Snapshot the current entries before clearing
  const entriesBeforeClear = getAuditLogEntries();

  // Record this clear event with the full snapshot
  const existing = safeParse(window.localStorage.getItem(AUDIT_CLEAR_HISTORY_KEY) || '[]', []);
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    actorEmail: actorEmail || 'Unknown admin',
    deletedEntries: entriesBeforeClear,
  };
  const next = [entry, ...(Array.isArray(existing) ? existing : [])].slice(0, MAX_CLEAR_HISTORY);
  window.localStorage.setItem(AUDIT_CLEAR_HISTORY_KEY, JSON.stringify(next));

  window.localStorage.removeItem(AUDIT_LOG_KEY);

  window.dispatchEvent(new Event('audit-log-updated'));
};

export const getClearHistory = () => {
  if (typeof window === 'undefined') return [];
  const stored = window.localStorage.getItem(AUDIT_CLEAR_HISTORY_KEY);
  const entries = safeParse(stored || '[]', []);
  return Array.isArray(entries) ? entries : [];
};
